/**
 * Page Sécurité - Administration
 * Comptes bloqués, 2FA, utilisateurs à risque
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Shield, Lock, Unlock, AlertTriangle, CheckCircle,
  XCircle, User, RefreshCw, ShieldCheck, ShieldOff,
} from 'lucide-react';
import { userAdminApi } from '../../infrastructure/api/userAdminApi';
import type { UserAdminDTO, UserAdminStatsDTO } from '../../application/dto/dtos';

export function SecurityPage() {
  const qc = useQueryClient();
  const [unlockingId, setUnlockingId] = useState<string | null>(null);

  const { data: statsData } = useQuery({
    queryKey: ['userAdmin', 'stats'],
    queryFn: () => userAdminApi.obtenirStatistiques(),
  });

  const { data: usersData, isLoading, refetch } = useQuery({
    queryKey: ['userAdmin', 'security'],
    queryFn: () => userAdminApi.listerUtilisateurs({ page: 1, limit: 100 }),
  });

  const deverrouillerMutation = useMutation({
    mutationFn: (id: string) => userAdminApi.deverrouillerCompte(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['userAdmin'] });
      setUnlockingId(null);
    },
  });

  const stats: UserAdminStatsDTO | undefined = statsData?.data?.statistiques;
  const users: UserAdminDTO[] = usersData?.data?.donnees?.utilisateurs ?? [];

  const bloques = users.filter(u => u.estVerrouille);
  const sans2FA = users.filter(u => !u.deuxFacteursActive && u.isActive);
  const nonVerifies = users.filter(u => !u.isVerified && u.isActive);

  const scoreSecurite = users.length === 0 ? 0 : Math.round(
    ((users.length - bloques.length) / users.length * 30) +
    ((users.length - sans2FA.length) / users.length * 40) +
    ((users.length - nonVerifies.length) / users.length * 30)
  );

  const scoreColor = scoreSecurite >= 80 ? 'text-success' : scoreSecurite >= 60 ? 'text-warning' : 'text-danger';
  const scoreBg = scoreSecurite >= 80
    ? 'bg-success-light border-success'
    : scoreSecurite >= 60
    ? 'bg-warning-light border-warning'
    : 'bg-danger-light border-danger';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-content">Sécurité</h1>
          <p className="mt-1 text-sm text-content-muted">
            État de sécurité des comptes et gestion des accès
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-3 py-2 text-sm text-content-muted border border-border rounded-lg hover:bg-surface-raised transition-colors"
        >
          <RefreshCw size={14} />
          Actualiser
        </button>
      </div>

      {/* Score de sécurité */}
      <div className={`rounded-lg border p-5 ${scoreBg}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield size={24} className={scoreColor} />
            <div>
              <p className="font-semibold text-content">Score de sécurité</p>
              <p className="text-xs text-content-subtle">Basé sur 2FA, vérification et comptes bloqués</p>
            </div>
          </div>
          <span className={`text-4xl font-extrabold ${scoreColor}`}>{scoreSecurite}<span className="text-lg">/100</span></span>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Comptes bloqués', value: bloques.length, icon: Lock, color: 'text-danger', bg: 'bg-danger-light' },
          { label: 'Sans 2FA', value: sans2FA.length, icon: ShieldOff, color: 'text-warning', bg: 'bg-warning-light' },
          { label: 'Non vérifiés', value: nonVerifies.length, icon: XCircle, color: 'text-warning', bg: 'bg-warning-light' },
          { label: 'Avec 2FA', value: stats?.avec2FA ?? 0, icon: ShieldCheck, color: 'text-success', bg: 'bg-success-light' },
        ].map((s) => (
          <div key={s.label} className="bg-surface rounded-lg border border-border p-4">
            <div className={`inline-flex p-2 rounded-lg ${s.bg} mb-2`}>
              <s.icon size={16} className={s.color} />
            </div>
            <p className="text-2xl font-bold text-content">{s.value}</p>
            <p className="text-xs text-content-subtle">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Comptes bloqués */}
      {bloques.length > 0 && (
        <div className="bg-surface rounded-lg border border-border overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-border bg-danger-light">
            <Lock size={16} className="text-danger" />
            <h3 className="font-semibold text-content text-sm">Comptes verrouillés</h3>
            <span className="ml-auto px-2 py-0.5 bg-danger-light text-danger text-xs rounded-full">{bloques.length}</span>
          </div>
          <div className="divide-y divide-border">
            {bloques.map(u => (
              <div key={u.idUtilisateur} className="flex items-center justify-between px-5 py-3">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-danger-light flex items-center justify-center shrink-0">
                    <User size={12} className="text-danger" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-content">{u.prenom} {u.nom}</p>
                    <p className="text-xs text-content-subtle">{u.email}</p>
                  </div>
                </div>
                <button
                  onClick={() => { setUnlockingId(u.idUtilisateur); deverrouillerMutation.mutate(u.idUtilisateur); }}
                  disabled={deverrouillerMutation.isPending && unlockingId === u.idUtilisateur}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-success bg-success-light border border-success rounded-lg hover:bg-success-light/80 transition-colors disabled:opacity-50"
                >
                  <Unlock size={12} />
                  Déverrouiller
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sans 2FA */}
      {sans2FA.length > 0 && (
        <div className="bg-surface rounded-lg border border-border overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-border bg-warning-light">
            <ShieldOff size={16} className="text-warning" />
            <h3 className="font-semibold text-content text-sm">Comptes sans double authentification</h3>
            <span className="ml-auto px-2 py-0.5 bg-warning-light text-warning text-xs rounded-full">{sans2FA.length}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-border">
                {sans2FA.map(u => (
                  <tr key={u.idUtilisateur} className="hover:bg-surface-raised">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-content-subtle shrink-0" />
                        <span className="font-medium text-content">{u.prenom} {u.nom}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-content-subtle text-xs">{u.email}</td>
                    <td className="px-5 py-3">
                      <span className="px-2 py-0.5 bg-surface-overlay text-content-muted text-xs rounded-full">{u.roleNom ?? u.role}</span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1 text-xs text-warning">
                        <ShieldOff size={10} /> 2FA désactivé
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tout est OK */}
      {!isLoading && bloques.length === 0 && sans2FA.length === 0 && (
        <div className="flex items-center gap-3 p-4 bg-success-light border border-success rounded-lg">
          <CheckCircle size={18} className="text-success shrink-0" />
          <p className="text-sm text-success">
            Aucun problème de sécurité détecté. Tous les comptes actifs ont le 2FA activé.
          </p>
        </div>
      )}
    </div>
  );
}

export default SecurityPage;
